import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { EquipmentStatusBadge } from '@components/atoms/EquipmentStatusBadge';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import { Checkbox } from '@components/atoms/Checkbox';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { EquipmentAsset, EquipmentStatus } from '@models/warehouse.model';
import {
  useEquipmentAssets,
  useCreateTaskEquipmentFromWarehouse,
} from '@hooks/api/warehouse';
import { useEquipmentReservations } from '@hooks/api/warehouse';
import ConstructionIcon from '@mui/icons-material/Construction';
import EventIcon from '@mui/icons-material/Event';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';

interface EquipmentOption extends SelectOption {
  equipment: EquipmentAsset;
}

interface TaskEquipmentReserveModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskStartDate?: string;
  taskEndDate?: string;
  isDelegated?: boolean;
  onSuccess?: () => void;
}

const formatDateDisplay = (date: string | Date) =>
  format(new Date(date), 'MMM dd, yyyy');

export const TaskEquipmentReserveModal: React.FC<TaskEquipmentReserveModalProps> = ({
  isOpen,
  onClose,
  taskId,
  taskStartDate,
  taskEndDate,
  isDelegated = false,
  onSuccess,
}) => {
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('23:59');
  const [notes, setNotes] = useState('');
  const [chargeToClient, setChargeToClient] = useState(false);
  const [clientPricePerHour, setClientPricePerHour] = useState<number | null>(null);

  const { data: allEquipment = [], isLoading: equipmentLoading } = useEquipmentAssets();
  const createTaskEquipment = useCreateTaskEquipmentFromWarehouse();

  // Fetch reservations for the selected asset
  const { data: assetReservations = [] } = useEquipmentReservations(
    selectedAssetId || undefined,
  );

  // Reset form and pre-fill task dates on open
  useEffect(() => {
    if (isOpen) {
      setSelectedAssetId('');
      setStartDate(taskStartDate || '');
      setStartTime('00:00');
      setEndDate(taskEndDate || '');
      setEndTime('23:59');
      setNotes('');
      setChargeToClient(false);
      setClientPricePerHour(null);
    }
  }, [isOpen, taskStartDate, taskEndDate]);

  // Filter out RETIRED equipment
  const selectableEquipment = useMemo(
    () => allEquipment.filter((eq) => eq.status !== EquipmentStatus.RETIRED),
    [allEquipment],
  );

  // Build options — only disable MAINTENANCE (AVAILABLE, RESERVED, IN_USE are selectable)
  const equipmentOptions: EquipmentOption[] = useMemo(
    () =>
      selectableEquipment.map((eq) => ({
        value: eq.assetId,
        label: `${eq.name}${eq.assetCode ? ` (${eq.assetCode})` : ''}`,
        equipment: eq,
        isDisabled: eq.status === EquipmentStatus.MAINTENANCE,
      })),
    [selectableEquipment],
  );

  const selectedEquipment = useMemo(
    () => selectableEquipment.find((eq) => eq.assetId === selectedAssetId),
    [selectableEquipment, selectedAssetId],
  );

  // Combine date + time into full ISO datetime
  const combinedStart = useMemo(() => {
    if (!startDate) return null;
    return new Date(`${startDate}T${startTime || '00:00'}:00`);
  }, [startDate, startTime]);

  const combinedEnd = useMemo(() => {
    if (!endDate) return null;
    return new Date(`${endDate}T${endTime || '23:59'}:00`);
  }, [endDate, endTime]);

  // Check for date/time overlap with existing reservations
  const overlapWarning = useMemo(() => {
    if (!combinedStart || !combinedEnd || assetReservations.length === 0) return null;
    const overlapping = assetReservations.filter((r) => {
      const rStart = new Date(r.startDate);
      const rEnd = new Date(r.endDate);
      return rStart <= combinedEnd && rEnd >= combinedStart;
    });
    if (overlapping.length === 0) return null;
    return overlapping;
  }, [combinedStart, combinedEnd, assetReservations]);

  const canSubmit =
    selectedAssetId &&
    startDate &&
    endDate &&
    selectedEquipment?.status !== EquipmentStatus.MAINTENANCE &&
    !overlapWarning &&
    !createTaskEquipment.isPending;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  // Custom option renderer
  const formatOptionLabel = (
    option: EquipmentOption,
    { context }: { context: 'menu' | 'value' },
  ) => {
    const eq = option.equipment;
    const isUnavailable = eq.status === EquipmentStatus.MAINTENANCE;

    if (context === 'value') {
      return (
        <div className="flex items-center gap-2">
          <ConstructionIcon
            className={isUnavailable ? 'text-gray-400' : 'text-indigo-500'}
            sx={{ fontSize: 16 }}
          />
          <span className="truncate">{eq.name}</span>
          <EquipmentStatusBadge status={eq.status} size="sm" />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 w-full py-0.5">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isUnavailable ? 'bg-gray-100' : 'bg-indigo-50'
          }`}>
          <ConstructionIcon
            className={isUnavailable ? 'text-gray-400' : 'text-indigo-500'}
            sx={{ fontSize: 16 }}
          />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span
            className={`text-sm font-medium truncate ${
              isUnavailable ? 'text-gray-400' : 'text-gray-900'
            }`}>
            {eq.name}
          </span>
          <span className="text-xs text-gray-500 truncate">
            {eq.assetCode ? `Code: ${eq.assetCode}` : eq.serialNumber ? `S/N: ${eq.serialNumber}` : ''}
            {eq.status === EquipmentStatus.RESERVED &&
              eq.reservations &&
              eq.reservations.length > 0 &&
              ` · ${eq.reservations.length} reservation(s)`}
            {eq.status === EquipmentStatus.IN_USE && ' · Currently in use'}
            {eq.status === EquipmentStatus.MAINTENANCE && ' · Under maintenance'}
          </span>
        </div>
        <div className="flex-shrink-0">
          <EquipmentStatusBadge status={eq.status} size="sm" />
        </div>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit || !combinedStart || !combinedEnd) return;

    try {
      // Create TaskEquipment from warehouse — this creates in RESERVED state
      // and reserves the underlying EquipmentAsset
      await createTaskEquipment.mutateAsync({
        taskId,
        assetId: selectedAssetId,
        startDate: combinedStart.toISOString(),
        endDate: combinedEnd.toISOString(),
        notes: notes.trim() || undefined,
        costPerHour: selectedEquipment?.hourlyRate || undefined,
        pricePerHour: chargeToClient
          ? (clientPricePerHour ?? selectedEquipment?.clientHourlyRate ?? undefined)
          : selectedEquipment?.clientHourlyRate || undefined,
        chargeToClient,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.errors?.[0]?.message || 'Failed to reserve equipment';
      toast.error(message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reserve Equipment"
      confirmText="Reserve"
      handleConfirm={handleSubmit}
      isLoading={createTaskEquipment.isPending}
      disableConfirm={!canSubmit}
      size="medium">
      <div className="flex flex-col gap-5">
        {/* Info Banner */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
          <EventIcon className="text-amber-500 mt-0.5" sx={{ fontSize: 18 }} />
          <p className="text-sm text-amber-700">
            Reserving equipment holds it for your task during the selected date and time range.
            Other tasks can still reserve the same equipment for non-overlapping time slots.
          </p>
        </div>

        {/* Equipment Selection */}
        <FormGroup label="Equipment" htmlFor="equipmentSelect">
          <ReactSelect
            options={equipmentOptions}
            value={
              selectedAssetId
                ? equipmentOptions.find((o) => o.value === selectedAssetId) || null
                : null
            }
            onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
              if (option && !Array.isArray(option) && 'value' in option) {
                setSelectedAssetId(option.value as string);
              } else {
                setSelectedAssetId('');
              }
            }}
            placeholder={equipmentLoading ? 'Loading equipment...' : 'Select equipment...'}
            isSearchable
            isClearable
            isLoading={equipmentLoading}
            formatOptionLabel={formatOptionLabel as any}
          />
        </FormGroup>

        {/* Existing Reservations for Selected Asset */}
        {selectedAssetId && assetReservations.length > 0 && (
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs font-medium text-gray-700 mb-2">
              Existing Reservations ({assetReservations.length})
            </p>
            <div className="space-y-1.5">
              {assetReservations.map((r) => (
                <div
                  key={r.reservationId}
                  className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">
                    {r.task?.name || 'Unknown task'}
                  </span>
                  <span className="text-gray-500">
                    {formatDateDisplay(r.startDate)} — {formatDateDisplay(r.endDate)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overlap Warning */}
        {overlapWarning && overlapWarning.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
            <WarningAmberIcon className="text-red-500 mt-0.5 flex-shrink-0" sx={{ fontSize: 18 }} />
            <div>
              <p className="text-sm font-medium text-red-700">Conflict Detected</p>
              <p className="text-sm text-red-600 mt-1">
                This reservation overlaps with {overlapWarning.length} existing reservation(s):
              </p>
              <ul className="mt-1 space-y-0.5">
                {overlapWarning.map((r) => (
                  <li key={r.reservationId} className="text-xs text-red-600">
                    • {r.task?.name || 'Unknown task'} ({formatDateDisplay(r.startDate)} — {formatDateDisplay(r.endDate)})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Equipment Details */}
        {selectedEquipment && selectedEquipment.status !== EquipmentStatus.MAINTENANCE && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <ConstructionIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{selectedEquipment.name}</p>
                <p className="text-xs text-gray-500">
                  {selectedEquipment.serialNumber || selectedEquipment.assetCode || ''}
                  {selectedEquipment.manufacturer ? ` · ${selectedEquipment.manufacturer}` : ''}
                  {selectedEquipment.model ? ` ${selectedEquipment.model}` : ''}
                </p>
              </div>
              <EquipmentStatusBadge status={selectedEquipment.status} size="sm" />
            </div>

            {/* Rates */}
            {selectedEquipment.hourlyRate != null ? (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="p-2 bg-white rounded-md">
                  <p className="text-xs text-gray-500">Hourly Rate</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(selectedEquipment.hourlyRate)}
                  </p>
                </div>
              </div>
            ) : null}

            {/* Warehouse location */}
            {selectedEquipment.warehouse && (
              <p className="text-xs text-gray-500 mt-2">
                Location: {selectedEquipment.warehouse.name}
              </p>
            )}
          </div>
        )}

        {/* Date & Time Range */}
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Start Date" htmlFor="startDate">
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            {taskStartDate && !startDate && (
              <p className="text-xs text-gray-400 mt-1">
                Task starts: {formatDateDisplay(taskStartDate)}
              </p>
            )}
          </FormGroup>
          <FormGroup label="Start Time" htmlFor="startTime">
            <Input
              id="startTime"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="End Date" htmlFor="endDate">
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
            {taskEndDate && !endDate && (
              <p className="text-xs text-gray-400 mt-1">
                Task ends: {formatDateDisplay(taskEndDate)}
              </p>
            )}
          </FormGroup>
          <FormGroup label="End Time" htmlFor="endTime">
            <Input
              id="endTime"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </FormGroup>
        </div>

        {/* Charge to Client (only for delegated tasks) */}
        {isDelegated && selectedEquipment && selectedEquipment.status !== EquipmentStatus.MAINTENANCE && (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-900">Charge to Client</p>
                <p className="text-xs text-gray-500">
                  {chargeToClient
                    ? 'Equipment usage will be billed to the client'
                    : 'Internal cost only — not billed to client'}
                </p>
              </div>
              <Checkbox
                checked={chargeToClient}
                onChange={(checked) => {
                  setChargeToClient(checked);
                  if (checked && clientPricePerHour === null) {
                    setClientPricePerHour(selectedEquipment.clientHourlyRate ?? null);
                  }
                }}
                name="chargeToClient"
              />
            </div>

            {chargeToClient && (
              <FormGroup label="Client Price Per Hour" htmlFor="clientPricePerHour">
                <InputCurrency
                  id="clientPricePerHour"
                  value={clientPricePerHour}
                  onChange={(val) => setClientPricePerHour(val)}
                  placeholder="0.00"
                />
                {selectedEquipment.clientHourlyRate != null &&
                  clientPricePerHour !== selectedEquipment.clientHourlyRate && (
                    <p className="text-xs text-gray-400 mt-1">
                      Default: {formatCurrency(selectedEquipment.clientHourlyRate)}/hr
                    </p>
                  )}
              </FormGroup>
            )}
          </div>
        )}

        {/* Notes */}
        <FormGroup label="Notes" htmlFor="reserveNotes">
          <textarea
            id="reserveNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reason for reservation or any additional notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            rows={3}
          />
        </FormGroup>

        {/* Empty state */}
        {!equipmentLoading && selectableEquipment.length === 0 && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
            <p className="text-sm text-amber-700">
              No equipment registered yet. Add equipment from the Equipment Assets page first.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TaskEquipmentReserveModal;
